import { useEffect, useState } from 'react';
import { usePrinterStore, type PrinterMode } from '@/features/printer/store';

/**
 * Best-effort label for a raw OS printer status code. Diagnostic-only — we
 * have learned the hard way that CUPS reports `5` (stopped) for healthy
 * printers in many real-world cases (paused queue, idle backend, prior
 * job error), so this label is shown in tooltips but does NOT gate the
 * "Installed" vs "Offline" decision.
 */
function osStatusLabel(status: number, platform: string): string | undefined {
  if (status === 0) return 'ready';
  if (platform === 'darwin' || platform === 'linux') {
    const cups: Record<number, string> = {
      3: 'idle',
      4: 'processing',
      5: 'stopped',
      6: 'authentication required',
    };
    return cups[status];
  }
  if (platform === 'win32') {
    const win: Record<number, string> = {
      1: 'paused',
      2: 'error',
      8: 'paper jam',
      16: 'out of paper',
      128: 'offline',
      512: 'busy',
      1024: 'printing',
      4096: 'not available',
    };
    return win[status];
  }
  return undefined;
}

export type PrinterStatus =
  | 'browser'
  | 'disabled'
  | 'checking'
  /** Network probe succeeded — printer is actually responding. */
  | 'connected'
  /** OS reports a printer is installed, but we cannot tell if it's powered on. */
  | 'installed'
  /** No OS-installed printer found and no network config. */
  | 'no-printer'
  /** Network config is set but the printer didn't answer (off / wrong IP / port closed). */
  | 'unreachable';

export interface PrinterStatusInfo {
  status: PrinterStatus;
  /** Whether the active probe was network or OS enumeration. */
  source: PrinterMode;
  /** Display-friendly summary, e.g. "EPSON TM-T20III" or "192.168.1.50:9100". */
  printerName: string | null;
  /** Number of OS printers detected when in `auto` mode; 0 in `network` mode. */
  printerCount: number;
  /** Most recent error message when status is `unreachable`. */
  error?: string;
  /** Latency in ms for the last successful network ping. */
  latencyMs?: number;
  /** Raw OS status code from `getPrintersAsync()`. Diagnostic only — too
   *  unreliable to gate UI state on; surfaced in the tooltip. */
  rawOsStatus?: number;
  /** Human label for the raw OS status, where we recognize it. */
  rawOsStatusLabel?: string;
  /** CUPS printer-state-reasons keywords (auto mode only). */
  reasons?: string[];
  /** Force an immediate re-probe (resets the polling cycle). */
  refresh: () => void;
}

/**
 * Polls the configured printer source and reports its status.
 *
 * - `mode: 'network'`: TCP-pings the saved host:port (default 9100). Works for
 *   ESC/POS LAN thermal printers regardless of OS-level driver presence.
 * - `mode: 'auto'`: enumerates OS-installed printers via Electron.
 * - Outside Electron: returns `browser` and never polls.
 */
export function usePrinterStatus(intervalMs = 60000): PrinterStatusInfo {
  const mode = usePrinterStore((s) => s.mode);
  const network = usePrinterStore((s) => s.network);
  const osPrinterName = usePrinterStore((s) => s.osPrinterName);
  const enabled = usePrinterStore((s) => s.enabled);

  // Bumping `nonce` re-triggers the polling effect immediately — used by the
  // manual Refresh button on the settings page.
  const [nonce, setNonce] = useState(0);
  const refresh = () => setNonce((n) => n + 1);

  const initialStatus: PrinterStatus =
    typeof window !== 'undefined' && window.electron
      ? enabled
        ? 'checking'
        : 'disabled'
      : 'browser';
  const [info, setInfo] = useState<PrinterStatusInfo>({
    status: initialStatus,
    source: mode,
    printerName: null,
    printerCount: 0,
    refresh,
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.electron) {
      setInfo({
        status: 'browser',
        source: mode,
        printerName: null,
        printerCount: 0,
        refresh,
      });
      return;
    }
    if (!enabled) {
      setInfo({
        status: 'disabled',
        source: mode,
        printerName: null,
        printerCount: 0,
        refresh,
      });
      return;
    }

    let cancelled = false;
    const electron = window.electron;

    const checkNetwork = async (cfg: { host: string; port: number }) => {
      try {
        const result = await electron.printer.testNetwork({
          host: cfg.host,
          port: cfg.port,
        });
        if (cancelled) return;
        if (result.connected) {
          setInfo({
            status: 'connected',
            source: 'network',
            printerName: `${cfg.host}:${cfg.port}`,
            printerCount: 0,
            latencyMs: result.latencyMs,
            refresh,
          });
        } else {
          setInfo({
            status: 'unreachable',
            source: 'network',
            printerName: `${cfg.host}:${cfg.port}`,
            printerCount: 0,
            error: result.error,
            refresh,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setInfo({
            status: 'unreachable',
            source: 'network',
            printerName: `${cfg.host}:${cfg.port}`,
            printerCount: 0,
            error: (err as Error).message,
            refresh,
          });
        }
      }
    };

    const checkOs = async () => {
      try {
        const printers = await electron.printer.listPrinters();
        if (cancelled) return;
        if (printers.length === 0) {
          setInfo({
            status: 'no-printer',
            source: 'auto',
            printerName: null,
            printerCount: 0,
            refresh,
          });
          return;
        }
        // Prefer the explicitly-selected printer; fall back to OS default;
        // fall back to first listed.
        const selected = osPrinterName
          ? printers.find((p) => p.name === osPrinterName)
          : null;
        const def = selected ?? printers.find((p) => p.isDefault) ?? printers[0];
        // If user picked a name that's no longer present, treat as no-printer.
        if (osPrinterName && !selected) {
          setInfo({
            status: 'no-printer',
            source: 'auto',
            printerName: osPrinterName,
            printerCount: printers.length,
            error: `Selected printer "${osPrinterName}" is no longer installed.`,
            refresh,
          });
          return;
        }
        const platform = window.electron?.platform ?? 'darwin';
        const rawOsStatus = def.status ?? 0;
        const printerName = def.displayName || def.name;

        if (platform === 'darwin' || platform === 'linux') {
          try {
            // Run CUPS + macOS probes in parallel. The CUPS probe is fast
            // (<200ms); the macOS one (system_profiler) is slow (~1–2s) but
            // catches printers that Print Center has marked "Offline, Last
            // Used" — which CUPS often misses for USB thermals.
            const [probe, macProbe] = await Promise.all([
              electron.printer.probeOs({ deviceName: def.name }),
              platform === 'darwin' && electron.printer.probeMacOs
                ? electron.printer.probeMacOs({ displayName: def.displayName || def.name })
                : Promise.resolve(null),
            ]);
            if (cancelled) return;

            const macSaysOffline = macProbe ? macProbe.online === false : false;

            if (probe.offlineLikely || macSaysOffline) {
              const reasonSummary = (() => {
                if (macSaysOffline && macProbe?.status) {
                  return `macOS reports: ${macProbe.status.toLowerCase()}`;
                }
                return probe.offlineReason ?? 'offline';
              })();
              const fixHint =
                probe.state === 'stopped'
                  ? ` Run \`cupsenable ${def.name}\` to re-enable.`
                  : '';
              setInfo({
                status: 'unreachable',
                source: 'auto',
                printerName,
                printerCount: printers.length,
                rawOsStatus,
                rawOsStatusLabel: reasonSummary,
                error: `${reasonSummary}.${fixHint}`,
                reasons: probe.reasons,
                refresh,
              });
              return;
            }
            if (probe.state === 'idle' || probe.state === 'printing') {
              const detail =
                probe.state === 'printing'
                  ? 'printing'
                  : macProbe?.status
                    ? `${macProbe.status.toLowerCase()} (macOS)`
                    : 'idle (CUPS)';
              setInfo({
                status: 'connected',
                source: 'auto',
                printerName,
                printerCount: printers.length,
                rawOsStatus,
                rawOsStatusLabel: detail,
                reasons: probe.reasons,
                refresh,
              });
              return;
            }
            // Unknown — fall through to "installed" (sky blue) below.
          } catch {
            // probe IPC failed (main hasn't been restarted with the new
            // handler, etc.) — fall through to "installed".
          }
        }

        setInfo({
          status: 'installed',
          source: 'auto',
          printerName,
          printerCount: printers.length,
          rawOsStatus,
          rawOsStatusLabel: osStatusLabel(rawOsStatus, platform),
          refresh,
        });
      } catch {
        if (!cancelled) {
          setInfo({
            status: 'no-printer',
            source: 'auto',
            printerName: null,
            printerCount: 0,
            refresh,
          });
        }
      }
    };

    const check = mode === 'network' && network ? () => checkNetwork(network) : checkOs;

    void check();
    const id = window.setInterval(check, intervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [intervalMs, mode, network?.host, network?.port, osPrinterName, enabled, nonce]);

  return info;
}
